'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  Home,
  BookOpen,
  Mic,
  Zap,
  Video,
  Users,
  MessageCircle,
  BookMarked,
  BarChart2,
  Award,
  Bell,
  User,
  CreditCard,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const navSections = [
  {
    label: 'Main',
    items: [
      { icon: Home, label: 'Dashboard', href: '/student-dashboard', badge: null, active: true },
      { icon: BookOpen, label: 'My Learning', href: '/student-dashboard', badge: null, active: false },
      { icon: Mic, label: 'Daily Practice', href: '/student-dashboard', badge: '1', active: false },
    ],
  },
  {
    label: 'Practice',
    items: [
      { icon: Zap, label: 'Speakathon', href: '/student-dashboard', badge: null, active: false },
      { icon: Video, label: 'Live Classes', href: '/student-dashboard', badge: 'Live', active: false },
      { icon: Users, label: 'Inner Circle', href: '/student-dashboard', badge: null, active: false },
      { icon: MessageCircle, label: 'Community', href: '/student-dashboard', badge: '12', active: false },
    ],
  },
  {
    label: 'Skills',
    items: [
      { icon: BookMarked, label: 'Vocabulary', href: '/student-dashboard', badge: null, active: false },
      { icon: BookOpen, label: 'GrammarMaster360', href: '/student-dashboard', badge: null, active: false },
      { icon: Mic, label: 'Pronunciation', href: '/student-dashboard', badge: null, active: false },
    ],
  },
  {
    label: 'Progress',
    items: [
      { icon: BarChart2, label: 'Progress Tracking', href: '/student-dashboard', badge: null, active: false },
      { icon: Award, label: 'Achievements', href: '/student-dashboard', badge: '3', active: false },
    ],
  },
];

const bottomItems = [
  { icon: Bell, label: 'Notifications', href: '/student-dashboard', badge: '5' },
  { icon: CreditCard, label: 'Membership', href: '/student-dashboard', badge: null },
  { icon: User, label: 'Profile', href: '/student-dashboard', badge: null },
  { icon: HelpCircle, label: 'Help & Support', href: '/student-dashboard', badge: null },
];

export default function DashboardSidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <aside
      className="flex flex-col h-full bg-card border-r border-border sidebar-transition overflow-hidden"
      style={{ width: collapsed ? 64 : 240 }}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-border flex-shrink-0 ${collapsed ? 'justify-center px-0' : 'px-4 gap-2.5'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-bold text-xs text-foreground leading-none truncate">Fluent English</span>
            <span className="font-semibold text-xs text-primary leading-none">Blueprint</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 no-scrollbar">
        {navSections.map((section) => (
          <div key={`section-${section.label}`} className="mb-4">
            {!collapsed && (
              <p className="px-4 mb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest">
                {section.label}
              </p>
            )}
            <div className="flex flex-col gap-0.5 px-2">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={`nav-item-${item.label}`}
                    href={item.href}
                    className={`nav-link relative group ${item.active ? 'nav-link-active' : ''} ${collapsed ? 'justify-center px-0 py-3' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon size={18} className="flex-shrink-0" />
                    {!collapsed && (
                      <span className="flex-1 truncate text-sm">{item.label}</span>
                    )}
                    {!collapsed && item.badge && (
                      <span
                        className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                          item.badge === 'Live' ?'bg-red-500 text-white' :'bg-primary/15 text-primary'
                        }`}
                      >
                        {item.badge}
                      </span>
                    )}
                    {collapsed && item.badge && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary" />
                    )}
                    {/* Tooltip for collapsed */}
                    {collapsed && (
                      <div className="absolute left-full ml-2 px-2 py-1 rounded-lg bg-foreground text-background text-xs font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        {item.label}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom items */}
      <div className="border-t border-border py-3 px-2 flex flex-col gap-0.5">
        {bottomItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={`bottom-${item.label}`}
              href={item.href}
              className={`nav-link relative group ${collapsed ? 'justify-center px-0 py-2.5' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon size={17} className="flex-shrink-0" />
              {!collapsed && <span className="flex-1 truncate text-sm">{item.label}</span>}
              {!collapsed && item.badge && (
                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-danger/15 text-danger">
                  {item.badge}
                </span>
              )}
              {collapsed && item.badge && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-danger" />
              )}
              {collapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 rounded-lg bg-foreground text-background text-xs font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  {item.label}
                </div>
              )}
            </Link>
          );
        })}
      </div>

      {/* Collapse toggle */}
      <div className="border-t border-border p-2">
        <button
          onClick={onToggle}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground ${collapsed ? '' : ''}`}
        >
          {collapsed ? <ChevronRight size={18} /> : (
            <>
              <ChevronLeft size={18} />
              <span className="text-xs font-medium">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}